/*
 * PROCLEANTOOLS — Recherche prédictive produits
 * Remplace assets/predictive-search.js du thème Speedfly.
 *
 * Objectifs :
 * - rechercher uniquement dans le catalogue produits ;
 * - conserver la pertinence native, la tolérance aux fautes et les synonymes Shopify ;
 * - différencier une recherche large (« produit vitre ») d'une recherche précise
 *   (« liquide vitre ») sans imposer une syntaxe Shopify restrictive ;
 * - privilégier les titres les plus proches dans les suggestions ;
 * - prendre en compte titre, type, marque, variante, tags, SKU et code-barres ;
 * - conserver les produits épuisés, mais après les produits disponibles ;
 * - annuler les anciennes requêtes pour éviter les résultats désynchronisés ;
 * - appliquer les mêmes paramètres aux recherches ordinateur et mobile.
 */
(function () {
  'use strict';

  var MINIMUM_CHARACTERS = 2;
  var RESULT_LIMIT = 8;
  var MAXIMUM_CACHE_ENTRIES = 20;
  var ORIGINAL_QUERY_PARAMETER = 'pct_q';
  var GENERIC_SEARCH_WORDS = {
    a: true,
    au: true,
    aux: true,
    avec: true,
    d: true,
    dans: true,
    de: true,
    des: true,
    du: true,
    en: true,
    et: true,
    gamme: true,
    gammes: true,
    qui: true,
    l: true,
    la: true,
    le: true,
    les: true,
    materiel: true,
    matériel: true,
    nettoyage: true,
    pour: true,
    produit: true,
    produits: true,
    professionnel: true,
    professionnelle: true,
    professionnelles: true,
    professionnels: true,
    solution: true,
    solutions: true,
    un: true,
    une: true,
    univers: true
  };

  /*
   * Corrections sûres propres au vocabulaire du catalogue. Shopify gère déjà
   * les fautes courantes ; cette liste ne corrige que les graphies pour
   * lesquelles les quatre premières lettres diffèrent ou les mots composés.
   */
  var SEARCH_TERM_CORRECTIONS = {
    barette: 'barrette',
    barettes: 'barrettes',
    telecopique: 'telescopique',
    telecopiques: 'telescopiques',
    'manche telescope': 'manche telescopique',
    'klean vitre': 'kleanvitre',
    'micro fibre': 'microfibre',
    'micro fibres': 'microfibres',
    mouileur: 'mouilleur',
    mouileurs: 'mouilleurs',
    vitreire: 'vitrerie'
  };

  /*
   * Intentions exprimées avec des mots qui ne figurent pas forcément dans les
   * titres produits. On les traduit vers le vocabulaire réel du catalogue.
   * Ainsi « sens bon » ne recherche plus le préfixe « bon » dans « bonnette ».
   */
  var SEARCH_INTENTS = {
    'bonne odeur': {
      query: '(desodorisant OR surodorant OR parfum OR parfumant OR odorant OR odeur)',
      rankingTokens: ['desodorisant', 'surodorant', 'parfum', 'parfumant', 'odorant', 'odeur']
    },
    'mauvaise odeur': {
      query: '(desodorisant OR surodorant OR parfum OR parfumant OR odorant OR odeur)',
      rankingTokens: ['desodorisant', 'surodorant', 'parfum', 'parfumant', 'odorant', 'odeur']
    },
    'sens bon': {
      query: '(desodorisant OR surodorant OR parfum OR parfumant OR odorant OR odeur)',
      rankingTokens: ['desodorisant', 'surodorant', 'parfum', 'parfumant', 'odorant', 'odeur']
    },
    'sent bon': {
      query: '(desodorisant OR surodorant OR parfum OR parfumant OR odorant OR odeur)',
      rankingTokens: ['desodorisant', 'surodorant', 'parfum', 'parfumant', 'odorant', 'odeur']
    },
    'laver les vitres': {
      query: '(raclette OR mouilleur OR grattoir OR microfibre)',
      rankingTokens: ['raclette', 'mouilleur', 'grattoir', 'microfibre']
    },
    'nettoyer les vitres': {
      query: '(raclette OR mouilleur OR grattoir OR microfibre)',
      rankingTokens: ['raclette', 'mouilleur', 'grattoir', 'microfibre']
    },
    'nettoyer les sols': {
      query: '(balai OR frange OR monobrosse OR autolaveuse)',
      rankingTokens: ['balai', 'frange', 'monobrosse', 'autolaveuse']
    },
    'eau pure': {
      query: '(osmoseur OR resine OR filtre OR hydropower)',
      rankingTokens: ['osmoseur', 'resine', 'filtre', 'hydropower']
    },
    'travail en hauteur': {
      query: '(perche OR echelle OR escabeau)',
      rankingTokens: ['perche', 'echelle', 'escabeau']
    }
  };

  function normaliseTerm(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function normaliseForComparison(value) {
    var text = normaliseTerm(value).toLocaleLowerCase('fr-FR');

    if (typeof text.normalize === 'function') {
      text = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    return text
      .replace(/[’']/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function routesRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    root = root || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function searchParameters() {
    return new URLSearchParams(window.location.search || '');
  }

  function tokeniseCustomerQuery(searchTerm) {
    return normaliseTerm(searchTerm)
      .replace(/[“”"():\\]+/g, ' ')
      .replace(/[’']/g, ' ')
      .split(/\s+/)
      .map(function (word) {
        return word.replace(/^[,;.!?]+|[,;.!?]+$/g, '');
      })
      .filter(function (word) {
        return word.length > 1;
      });
  }

  function prepareLiteralQuery(searchTerm) {
    var cleanTerm = normaliseTerm(searchTerm);
    var words = tokeniseCustomerQuery(cleanTerm);
    var usefulWords;
    var correctedTerm;

    if (!words.length) return cleanTerm;

    usefulWords = words.filter(function (word) {
      return !GENERIC_SEARCH_WORDS[word.toLocaleLowerCase('fr-FR')];
    });

    /* Une recherche composée uniquement d'un mot générique reste utilisable. */
    if (!usefulWords.length) usefulWords = words;

    correctedTerm = usefulWords.join(' ');

    Object.keys(SEARCH_TERM_CORRECTIONS).forEach(function (incorrectTerm) {
      var expression = new RegExp(
        '(^|\\s)' + incorrectTerm.replace(/ /g, '\\s+') + '(?=\\s|$)',
        'gi'
      );

      correctedTerm = correctedTerm.replace(expression, function (match, prefix) {
        return prefix + SEARCH_TERM_CORRECTIONS[incorrectTerm];
      });
    });

    return normaliseTerm(correctedTerm);
  }

  function getSearchIntent(searchTerm) {
    return SEARCH_INTENTS[normaliseForComparison(prepareLiteralQuery(searchTerm))] || null;
  }

  function prepareShopifyQuery(searchTerm) {
    var literalQuery = prepareLiteralQuery(searchTerm);
    var intent = getSearchIntent(literalQuery);
    return intent ? intent.query : literalQuery;
  }

  function looksLikeReference(searchTerm) {
    return /\d/.test(searchTerm) || /[-_/]/.test(searchTerm);
  }

  function getRankingTokens(searchTerm) {
    var intent = getSearchIntent(searchTerm);
    return intent
      ? intent.rankingTokens
      : normaliseForComparison(prepareLiteralQuery(searchTerm)).split(/\s+/).filter(Boolean);
  }

  function scorePredictiveItem(item, searchTerm, originalIndex) {
    var heading = item.querySelector(
      '.predictive-search__item-heading, [class*="item-heading"], h3, h4'
    );
    var title = normaliseForComparison(heading ? heading.textContent : item.textContent);
    var intent = getSearchIntent(searchTerm);
    var query = intent ? '' : normaliseForComparison(prepareLiteralQuery(searchTerm));
    var tokens = getRankingTokens(searchTerm);
    var score = 0;
    var matchedTokens = 0;

    if (!title || (!query && !tokens.length)) {
      return { item: item, score: 0, index: originalIndex };
    }

    if (query) {
      if (title === query) score += 10000;
      if (title.indexOf(query) === 0) score += 5000;
      if (title.indexOf(query) !== -1) score += 3000;
    }

    tokens.forEach(function (token) {
      if (title.split(' ').indexOf(token) !== -1) {
        score += 500;
        matchedTokens += 1;
      } else if (title.indexOf(token) !== -1) {
        score += 250;
        matchedTokens += 1;
      }
    });

    if (intent && matchedTokens) score += 3500;
    if (!intent && tokens.length && matchedTokens === tokens.length) score += 2000;
    score += matchedTokens * 100;

    if (/\b(?:épuisé|epuise|indisponible|sold out)\b/i.test(item.textContent)) {
      score -= 100;
    }

    return { item: item, score: score, index: originalIndex };
  }

  function rankPredictiveProducts(section, searchTerm) {
    var items = Array.prototype.slice.call(
      section.querySelectorAll('.predictive-search__list-item')
    );
    var parents = [];

    items.forEach(function (item) {
      if (item.parentNode && parents.indexOf(item.parentNode) === -1) {
        parents.push(item.parentNode);
      }
    });

    parents.forEach(function (parent) {
      var parentItems = items.filter(function (item) {
        return item.parentNode === parent;
      });

      parentItems
        .map(function (item, index) {
          return scorePredictiveItem(item, searchTerm, index);
        })
        .sort(function (first, second) {
          return second.score - first.score || first.index - second.index;
        })
        .forEach(function (entry) {
          parent.appendChild(entry.item);
        });
    });
  }

  function originalSearchTermFromUrl() {
    var parameters = searchParameters();
    var originalTerm = normaliseTerm(parameters.get(ORIGINAL_QUERY_PARAMETER));
    if (originalTerm) return originalTerm;

    return normaliseTerm(parameters.get('q'));
  }

  function buildFullSearchUrl(searchTerm) {
    var term = normaliseTerm(searchTerm);
    var parameters = new URLSearchParams();

    parameters.set('q', prepareShopifyQuery(term));
    parameters.set('type', 'product');
    parameters.set('options[prefix]', 'last');
    parameters.set('options[unavailable_products]', 'last');
    parameters.set('sort_by', 'relevance');
    parameters.set(ORIGINAL_QUERY_PARAMETER, term);

    return routesRoot() + 'search?' + parameters.toString();
  }

  function redirectLegacySearchPage() {
    if (window.location.pathname.indexOf('/search') === -1) return false;

    var parameters = searchParameters();
    var currentTerm = normaliseTerm(parameters.get('q'));

    if (!currentTerm) return false;

    var originalTerm = normaliseTerm(parameters.get(ORIGINAL_QUERY_PARAMETER));
    if (originalTerm) {
      if (currentTerm === prepareShopifyQuery(originalTerm)) return false;
      window.location.replace(buildFullSearchUrl(originalTerm));
      return true;
    }

    window.location.replace(buildFullSearchUrl(currentTerm));
    return true;
  }

  function findFormControl(form, name) {
    if (!form || !form.elements) return null;

    var controls = form.elements;
    for (var index = 0; index < controls.length; index += 1) {
      if (controls[index].name === name) return controls[index];
    }

    return null;
  }

  function setHiddenValue(form, name, value) {
    if (!form) return;

    var input = findFormControl(form, name);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }

    input.value = value;
  }

  function prepareSearchForm(form) {
    if (!form) return;
    if (form.matches('[data-pct-search-filters-form], [data-pct-facets-form]')) return;

    setHiddenValue(form, 'type', 'product');
    setHiddenValue(form, 'options[prefix]', 'last');
    setHiddenValue(form, 'options[unavailable_products]', 'last');
    setHiddenValue(form, 'sort_by', 'relevance');

    if (form.dataset.pctSearchPrepared === 'true') return;
    form.dataset.pctSearchPrepared = 'true';

    form.addEventListener('submit', function (event) {
      var input = form.querySelector('input[type="search"], input[name="q"]');
      if (!input) return;

      var term = normaliseTerm(input.value);
      if (!term) {
        event.preventDefault();
        input.focus();
        return;
      }

      event.preventDefault();
      window.location.assign(buildFullSearchUrl(term));
    });
  }

  function restoreFriendlySearchText(scope) {
    if (window.location.pathname.indexOf('/search') === -1) return;

    var originalTerm = originalSearchTermFromUrl();
    if (!originalTerm) return;

    var parameters = searchParameters();
    var technicalTerm = normaliseTerm(parameters.get('q'));
    var context = scope && scope.querySelectorAll ? scope : document;
    var inputs = context.querySelectorAll('input[type="search"]');

    Array.prototype.forEach.call(inputs, function (input) {
      input.value = originalTerm;
    });

    if (!technicalTerm || technicalTerm === originalTerm) return;

    var headings = context.querySelectorAll(
      'h1, h2, h3, .section-header__title, .search-title, [data-search-heading]'
    );

    Array.prototype.forEach.call(headings, function (heading) {
      var walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
      var node;

      while ((node = walker.nextNode())) {
        if (node.nodeValue.indexOf(technicalTerm) !== -1) {
          node.nodeValue = node.nodeValue.split(technicalTerm).join(originalTerm);
        }
      }
    });

    if (document.title.indexOf(technicalTerm) !== -1) {
      document.title = document.title.split(technicalTerm).join(originalTerm);
    }
  }

  function prepareAllSearchForms(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var forms = context.querySelectorAll('form[action*="/search"]');

    Array.prototype.forEach.call(forms, prepareSearchForm);
    restoreFriendlySearchText(scope);
  }

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.input = null;
      this.form = null;
      this.results = null;
      this.abortController = null;
      this.requestNumber = 0;
      this.cache = new Map();
      this.debounceTimer = 0;
      this.isReady = false;

      this.handleInput = this.handleInput.bind(this);
      this.handleInputKeydown = this.handleInputKeydown.bind(this);
      this.handleResultsKeydown = this.handleResultsKeydown.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
    }

    connectedCallback() {
      if (this.isReady) return;

      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.results = this.querySelector('#predictive-search');

      if (!this.input || !this.form || !this.results) return;

      this.isReady = true;
      prepareSearchForm(this.form);

      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('spellcheck', 'false');
      this.input.setAttribute('aria-expanded', 'false');
      this.results.setAttribute('aria-hidden', 'true');

      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('keydown', this.handleInputKeydown);
      this.results.addEventListener('keydown', this.handleResultsKeydown);
      document.addEventListener('click', this.handleDocumentClick);
    }

    disconnectedCallback() {
      window.clearTimeout(this.debounceTimer);
      this.cancelPendingRequest();

      if (this.input) {
        this.input.removeEventListener('input', this.handleInput);
        this.input.removeEventListener('keydown', this.handleInputKeydown);
      }

      if (this.results) {
        this.results.removeEventListener('keydown', this.handleResultsKeydown);
      }

      document.removeEventListener('click', this.handleDocumentClick);
      this.isReady = false;
    }

    handleInput() {
      var searchTerm = normaliseTerm(this.input.value);
      window.clearTimeout(this.debounceTimer);

      if (searchTerm.length < MINIMUM_CHARACTERS) {
        this.cancelPendingRequest();
        this.close();
        return;
      }

      this.debounceTimer = window.setTimeout(function () {
        this.getSearchResults(searchTerm);
      }.bind(this), 240);
    }

    handleInputKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'ArrowDown' || !this.isOpen()) return;

      var firstResult = this.getResultLinks()[0];
      if (firstResult) {
        event.preventDefault();
        firstResult.focus();
      }
    }

    handleResultsKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        this.input.focus();
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      var links = this.getResultLinks();
      var currentIndex = links.indexOf(document.activeElement);
      if (currentIndex < 0) return;

      event.preventDefault();
      var direction = event.key === 'ArrowDown' ? 1 : -1;
      var nextIndex = currentIndex + direction;

      if (nextIndex < 0) {
        this.input.focus();
      } else if (nextIndex >= links.length) {
        links[0].focus();
      } else {
        links[nextIndex].focus();
      }
    }

    handleDocumentClick(event) {
      if (!this.contains(event.target)) this.close();
    }

    getResultLinks() {
      if (!this.results) return [];
      return Array.prototype.slice.call(this.results.querySelectorAll('a[href]'));
    }

    isOpen() {
      return this.input && this.input.getAttribute('aria-expanded') === 'true';
    }

    cancelPendingRequest() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    buildRequestUrl(searchTerm) {
      var parameters = new URLSearchParams();
      var searchableFields = [
        'title',
        'product_type',
        'variants.title',
        'vendor',
        'tag'
      ];

      if (looksLikeReference(searchTerm)) {
        searchableFields.push('variants.sku', 'variants.barcode');
      }

      parameters.set('q', prepareShopifyQuery(searchTerm));
      parameters.set('resources[type]', 'product');
      parameters.set('resources[limit]', String(RESULT_LIMIT));
      parameters.set('resources[limit_scope]', 'all');
      parameters.set('resources[options][unavailable_products]', 'last');
      parameters.set('resources[options][fields]', searchableFields.join(','));
      parameters.set('section_id', 'predictive-search');

      return routesRoot() + 'search/suggest?' + parameters.toString();
    }

    fullSearchUrl(searchTerm) {
      return buildFullSearchUrl(searchTerm);
    }

    getSearchResults(searchTerm) {
      var term = normaliseTerm(searchTerm);
      var cacheKey = term.toLocaleLowerCase();

      if (term.length < MINIMUM_CHARACTERS) {
        this.close();
        return;
      }

      if (this.cache.has(cacheKey)) {
        this.renderResults(this.cache.get(cacheKey), term);
        return;
      }

      this.cancelPendingRequest();
      this.abortController = new AbortController();
      this.requestNumber += 1;

      var activeRequest = this.requestNumber;
      var activeController = this.abortController;

      this.input.setAttribute('aria-busy', 'true');
      this.results.setAttribute('aria-busy', 'true');
      this.classList.add('is-loading');

      fetch(this.buildRequestUrl(term), {
        signal: activeController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Recherche indisponible (' + response.status + ')');
          return response.text();
        })
        .then(function (html) {
          if (activeRequest !== this.requestNumber) return;
          if (normaliseTerm(this.input.value).toLocaleLowerCase() !== cacheKey) return;

          var documentFragment = new DOMParser().parseFromString(html, 'text/html');
          var section = documentFragment.querySelector('#shopify-section-predictive-search');
          if (!section) throw new Error('Réponse de recherche invalide');

          var heading = section.querySelector('#predictive-search-products');
          if (heading) heading.textContent = 'Produits';

          rankPredictiveProducts(section, term);

          Array.prototype.forEach.call(
            section.querySelectorAll('a[href*="/search"]'),
            function (link) {
              link.href = this.fullSearchUrl(term);
            }.bind(this)
          );

          var products = section.querySelectorAll('.predictive-search__list-item');
          if (products.length === 0) {
            section.innerHTML = '';
            var emptyMessage = documentFragment.createElement('p');
            emptyMessage.className = 'pct-predictive-search__empty';
            emptyMessage.textContent = 'Aucun produit trouvé pour « ' + term + ' ».';
            section.appendChild(emptyMessage);
          }

          var footer = documentFragment.createElement('div');
          footer.className = 'pct-predictive-search__footer';

          var allResultsLink = documentFragment.createElement('a');
          allResultsLink.className = 'pct-predictive-search__all';
          allResultsLink.href = this.fullSearchUrl(term);
          allResultsLink.textContent = 'Voir tous les résultats produits';
          footer.appendChild(allResultsLink);
          section.appendChild(footer);

          var markup = section.innerHTML;
          this.cache.set(cacheKey, markup);

          if (this.cache.size > MAXIMUM_CACHE_ENTRIES) {
            this.cache.delete(this.cache.keys().next().value);
          }

          this.renderResults(markup, term);
        }.bind(this))
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          this.close();
        }.bind(this))
        .finally(function () {
          if (activeRequest !== this.requestNumber) return;

          this.classList.remove('is-loading');
          this.input.removeAttribute('aria-busy');
          this.results.removeAttribute('aria-busy');

          if (this.abortController === activeController) {
            this.abortController = null;
          }
        }.bind(this));
    }

    renderResults(markup, searchTerm) {
      if (normaliseTerm(this.input.value).toLocaleLowerCase() !== searchTerm.toLocaleLowerCase()) {
        return;
      }

      this.results.innerHTML = markup;
      this.open();
    }

    open() {
      if (!this.results || !this.results.innerHTML.trim()) return;

      this.results.style.display = 'block';
      this.results.setAttribute('aria-hidden', 'false');
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (!this.results || !this.input) return;

      this.results.style.display = 'none';
      this.results.setAttribute('aria-hidden', 'true');
      this.input.setAttribute('aria-expanded', 'false');
    }
  }

  if (!window.customElements.get('predictive-search')) {
    window.customElements.define('predictive-search', PredictiveSearch);
  }

  function openSearch() {
    var overlay = document.getElementById('search-overlay');
    if (overlay) overlay.style.display = 'block';

    window.setTimeout(function () {
      var searchInput = overlay
        ? overlay.querySelector('input[type="search"]')
        : document.querySelector('input[type="search"]');

      if (searchInput) searchInput.focus();
    }, 180);
  }

  function closeSearch() {
    var overlay = document.getElementById('search-overlay');
    if (overlay) overlay.style.display = 'none';

    Array.prototype.forEach.call(
      document.querySelectorAll('predictive-search'),
      function (component) {
        if (typeof component.close === 'function') component.close();
      }
    );
  }

  window.openSearch = openSearch;
  window.closeSearch = closeSearch;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      if (redirectLegacySearchPage()) return;
      prepareAllSearchForms(document);
    }, { once: true });
  } else {
    if (!redirectLegacySearchPage()) prepareAllSearchForms(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    prepareAllSearchForms(event.target);
  });
}());
